import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import { useTranslation } from 'react-i18next';
import { PictureTile } from '../../../components/shared/tiles/PictureTile';
import AllergenChips from '../../RecipeV2/components/RecipeV2AllergenChips';
import DietaryAttributeChips from '../../RecipeV2/components/DietaryAttributeChips';
import { DietaryCategory, RecipeV2 } from '../types';

interface RecipeBankCardProps {
  recipe: RecipeV2;
  onOpen: (recipe: RecipeV2) => void;
}

/**
 * Recipe Bank catalogue card. Deliberately excludes cost, cost per portion, suppliers,
 * internal IDs and edit/lifecycle controls - this is a browse surface, not authoring.
 */
export const RecipeBankCard = ({ recipe, onOpen }: RecipeBankCardProps) => {
  const { t } = useTranslation('recipeV2');
  const theme = useTheme();
  const dietaryBadges = (recipe.dietaryAttributes ?? [])
    .filter((attribute) => attribute.category !== DietaryCategory.Allergen)
    .slice(0, 3);
  const primaryYield = recipe.yields?.[0];

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow: '0 1px 2px 1px #1018280D',
        borderColor: theme.palette.custom.gray[300],
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'none',
        '&:hover': { borderColor: theme.palette.custom.gray[400] },
      }}
    >
      <CardActionArea
        disableRipple
        onClick={() => onOpen(recipe)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          height: '100%',
          transition: 'none',
          '&:hover': { backgroundColor: 'transparent' },
          '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
          '& .MuiTouchRipple-root': { display: 'none' },
        }}>
        <Box sx={{ height: 160, display: 'flex' }}>
          <PictureTile
            src={recipe.mediaUrl}
            alt={recipe.description}
            objectFit="cover"
            fallbackLabel={t('recipeBank.noImage', 'No recipe image')}
            fallback={(
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ width: '100%', height: '100%' }}
              >
                <ImageOutlined sx={{ fontSize: 32 }} />
                <Typography variant="textSM">{t('recipeBank.noImage', 'No recipe image')}</Typography>
              </Stack>
            )}
            minHeight={0}
            sx={{ height: '100%', width: '100%' }}
          />
        </Box>
        <CardContent sx={{ flexGrow: 1, width: '100%' }}>
          <Typography
            variant="textMD"
            fontWeight={600}
            sx={{
              mb: 2,
              minHeight: 48,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {recipe.description || ' '}
          </Typography>
          <Stack direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1 }}>
            {recipe.primaryCategory && (
              <Chip label={recipe.primaryCategory}
                size="small" />
            )}
          </Stack>
          {primaryYield && (
            <Typography variant="textSM"
              color="text.secondary"
              sx={{ mb: 1 }}>
              {primaryYield.quantity} {primaryYield.uom}
            </Typography>
          )}
          <Box sx={{ mb: 1 }}>
            <AllergenChips allergens={recipe.allergenSummary ?? []}
              max={4}
              size="small" />
          </Box>
          <Stack direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap>
            <DietaryAttributeChips attributes={dietaryBadges}
              max={3}
              size="small"
              showName />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default RecipeBankCard;
